<template>
  <div :id="id">
    <div
      class="bg-oaiGray-900 video-js vjs-live vjs-4-3"
      :class="{
        'vjs-waiting': loading,
        'vjs-user-active': isUserActive,
        'vjs-user-inactive': !isUserActive,
        'vjs-has-started': imageUrl,
        'vjs-playing': isPlaying,
        'vjs-paused': !isPlaying,
        'vjs-controls-enabled': showControls,
      }"
      @mouseenter="
        stopUserActiveInitialTimer();
        isUserActive = true;
      "
      @mouseleave="isUserActive = false"
      @mousemove="isUserActive = true"
      ref="containerRef"
    >
      <a
        class="image-content select-none block"
        :href="imageUrl ? imageUrl : ''"
        target="_blank"
        rel="noreferrer"
        :data-pswp-width="getImageSize().width"
        :data-pswp-height="getImageSize().height"
      >
        <img
          v-if="imageUrl"
          :src="imageUrl"
          alt="live image"
          class="h-full"
          @dblclick="handleFullScreenClick"
          @click="handleImageClick"
        />
      </a>

      <div
        v-if="hasError"
        class="error-content"
        :style="`${fullWindow4to3RatioDimensionStyle}; height: auto;`"
      >
        <div
          class="rounded-md bg-yellow-400/80 p-4 text-center text-xs sm:text-sm text-gray-50 flex-1 m-5"
        >
          {{ noSrcMessage || $t("video_player.unable_to_play_message") }}
        </div>
      </div>
      <div class="vjs-loading-spinner" v-if="loading" />
      <div
        class="vjs-control-bar"
        :class="{ 'pr-5': addRightSpaceToControlBar }"
        v-if="showControls"
        @click.stop
      >
        <button
          class="vjs-play-control vjs-control vjs-button"
          :class="{
            'vjs-playing': isPlaying,
            'vjs-paused': !isPlaying,
          }"
          type="button"
          :title="isPlaying ? 'Pause' : 'Play'"
          aria-disabled="false"
          @click="handlePlayClick"
        >
          <span class="vjs-icon-placeholder" aria-hidden="true"></span
          ><span class="vjs-control-text" aria-live="polite">Play</span>
        </button>
        <div class="vjs-live-control vjs-control">
          <div class="vjs-live-display" aria-live="off">
            <span class="vjs-control-text">Stream Type&nbsp;</span>LIVE
          </div>
        </div>
        <div class="flex items-center cursor-pointer" @click="loadLiveImage()">
          <ArrowPathIcon class="w-4 h-4 text-white" />
        </div>
        <button
          class="vjs-fullscreen-control vjs-control vjs-button"
          type="button"
          :title="lightbox?.pswp?.isOpen ? 'Non-Fullscreen' : 'Fullscreen'"
          aria-disabled="false"
          @click="handleFullScreenClick"
        >
          <span class="vjs-icon-placeholder" aria-hidden="true"></span
          ><span class="vjs-control-text" aria-live="polite">Fullscreen</span>
        </button>
      </div>
    </div>
  </div>

  <Teleport to="#loading-spinner">
    <div class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100000000]">
      <LoadingSpinner v-if="loading && lightbox?.pswp?.isOpen" color="text-white" />
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { ArrowPathIcon } from "@heroicons/vue/24/outline";
import mobile from "is-mobile";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import { onMounted, onUnmounted, PropType, ref } from "vue";
import LoadingSpinner from "shared/components/loading_state/LoadingSpinner.vue";
import { useCurrentCustomerName, useCurrentSiteId } from "shared/composables/project";
import { useFullWindow4to3RatioDimensionStyle } from "shared/composables/screen";
import StreamRepository from "shared/repositories/StreamRepository";
import logger from "shared/services/logger";

const imageUrl = ref<string | null>(null);
const loading = ref(false);
const hasError = ref(false);
const isUserActive = ref(true);
const isPlaying = ref(false);
const containerRef = ref<HTMLDivElement | null>(null);
const userActiveInitialTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const refreshTimer = ref<ReturnType<typeof setInterval> | null>(null);
const abortController = ref<AbortController | null>(null);

const isMobileDevice = mobile();
const currentCustomerName = useCurrentCustomerName();
const currentSiteId = useCurrentSiteId();
const fullWindow4to3RatioDimensionStyle = useFullWindow4to3RatioDimensionStyle();

const props = defineProps({
  cameraId: {
    type: String,
    required: true,
  },
  showControls: {
    type: Boolean,
    required: false,
    default: true,
  },
  noSrcMessage: {
    type: String as PropType<string | null>,
    required: false,
  },
  addRightSpaceToControlBar: {
    type: Boolean,
    required: false,
  },
  reloadIntervalInMs: {
    type: Number,
    required: false,
    default: 60 * 1000,
  },
});

const emit = defineEmits(["lowBandWidthError"]);

const getImageSize = () => {
  const browserWidth = window.innerWidth;
  const browserHeight = window.innerHeight;
  const ratio = 4 / 3;
  let width = browserWidth;
  let height = browserHeight;

  if (width / height > ratio) {
    width = height * ratio;
  } else {
    height = width / ratio;
  }
  return { width: width, height: height };
};

const loadLiveImage = () => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  hasError.value = false;

  abortController.value = new AbortController();

  StreamRepository.loadLiveImage(
    currentCustomerName,
    currentSiteId,
    props.cameraId,
    abortController.value.signal,
  )
    .then((imageArrayBuffer) => {
      if (imageArrayBuffer) {
        if (imageUrl.value) {
          window.URL.revokeObjectURL(imageUrl.value);
        }
        const blob = new Blob([imageArrayBuffer], { type: "image/jpeg" });
        imageUrl.value = window.URL.createObjectURL(blob);

        const galleryImage = lightbox.value?.pswp?.currSlide?.content?.element as HTMLInputElement;
        if (galleryImage) {
          galleryImage.src = imageUrl.value;
        }

        startUserActiveInitialTimer();
      } else {
        hasError.value = true;
      }
    })
    .catch((error) => {
      hasError.value = true;
      logger.error(error);
    })
    .finally(() => {
      if (!imageUrl.value) {
        emit("lowBandWidthError");
      }
      loading.value = false;
    });
};

const handleImageClick = (e: MouseEvent) => {
  e.stopPropagation();
  e.preventDefault();

  if (!props.showControls) {
    return;
  }
  if (isPlaying.value) {
    stopPlay();
  } else {
    startPlay({ dontReload: true });
  }
};

const handlePlayClick = () => {
  if (isPlaying.value) {
    stopPlay();
  } else {
    startPlay();
  }
};

const handleFullScreenClick = () => {
  if (!lightbox.value) {
    return;
  }

  const pswp = lightbox.value.pswp;
  if (pswp?.isOpen) {
    pswp?.close();
  } else {
    lightbox.value?.loadAndOpen(0);
  }
};

const startUserActiveInitialTimer = () => {
  if (userActiveInitialTimer.value === null && isPlaying.value) {
    userActiveInitialTimer.value = setTimeout(() => {
      isUserActive.value = false;
    }, 3000);
  }
};

const stopUserActiveInitialTimer = () => {
  if (userActiveInitialTimer.value !== null) {
    clearTimeout(userActiveInitialTimer.value);
  }
};

const startPlay = (options?: { dontReload: boolean }) => {
  isPlaying.value = true;
  if (!options?.dontReload) {
    loadLiveImage();
  }
  refreshTimer.value = setInterval(() => {
    loadLiveImage();
  }, props.reloadIntervalInMs);
};

const stopPlay = () => {
  isPlaying.value = false;
  if (refreshTimer.value !== null) {
    clearInterval(refreshTimer.value);
  }
};

onMounted(() => {
  mountPhotoSwipe();
  if (isMobileDevice) {
    loadLiveImage();
  } else {
    startPlay();
  }
});

onUnmounted(() => {
  stopUserActiveInitialTimer();
  stopPlay();
  if (abortController.value && loading.value) {
    abortController.value.abort();
  }

  lightbox.value?.destroy();
});

const lightbox = ref<PhotoSwipeLightbox | null>(null);
const id = `gallery-${props.cameraId}`;
const refreshButtonSvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="size-6 mx-auto" stroke-width="2.5" stroke="#fff">
  <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
</svg>
`;
const compressButtonSvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="#fff" viewBox="0 0 500 500" class="size-6">
    <path d="M160 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 64-64 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l96 0c17.7 0 32-14.3 32-32l0-96zM32 320c-17.7 0-32 14.3-32 32s14.3 32 32 32l64 0 0 64c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96c0-17.7-14.3-32-32-32l-96 0zM352 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7 14.3 32 32 32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-64 0 0-64zM320 320c-17.7 0-32 14.3-32 32l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-64 64 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0z"/>
</svg>`;

const mountPhotoSwipe = () => {
  if (lightbox.value) {
    return;
  }

  const lightboxInstance = new PhotoSwipeLightbox({
    gallery: `#${id}`,
    children: "a",
    pswpModule: () => import("photoswipe"),
    wheelToZoom: true,
    closeSVG: compressButtonSvg,
    zoom: false,
  });

  lightboxInstance.on("uiRegister", () => {
    lightboxInstance.pswp?.ui?.registerElement({
      name: "refresh-button",
      order: 8,
      isButton: true,
      tagName: "a",
      className: "flex items-center justify-center",
      html: refreshButtonSvg,
      onClick: loadLiveImage,
    });
  });

  lightboxInstance.init();
  lightbox.value = lightboxInstance;
};
</script>

<style scoped>
.error-content {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, 0);
  width: 100%;
}

.image-content {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /*
    This is not a perfect solution, as it won't work for different aspect ratios (e.g. kauricab/002).
    But the good thing is, that the image never overflows the container; in the worse case, it might be a bit zoomed in.
  */
  aspect-ratio: 4/3;
}
</style>
